import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { ICalendar, IWeek } from '@/types/calendar';

import { classList, getWeekDaysForCalendar } from '@/helpers';
import { useScrollIntoView, useShowAvailableShifts, useTimezone, useWeek } from '@/hooks';
import { media } from '@/styles/theme';
import {
  CALENDAR_MESSAGE_HEIGHT,
  CALENDAR_MESSAGE_HEIGHT_MOBILE,
  FULL_WEEKDAY_MONTH_DAY_FORMAT,
  NAVBAR_HEIGHT,
  TIMELINE_COLUMN_WIDTH,
  TIMELINE_COLUMN_WIDTH_MOBILE,
  Z_INDEX_WEEKDAY_HEADER,
} from '@/config/variables';
import { CalendarMessage, CalendarWeekContainer, TimelineColumn } from '@/components/common';
import { Week } from '@/classes/Week';

// TODO move to proper file
const getIsEditAvailability = (searchQuery: string) => {
  const query = new URLSearchParams(searchQuery);
  const queryParam = query.get('editAvailability');

  return queryParam?.toLocaleLowerCase() === 'true';
};

const Calendar = ({ Weekday, containerRef, isLoading, ignoreDLS }: ICalendar): React.ReactElement => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { isShowAvailableShifts } = useShowAvailableShifts();
  const { selectedDays, selectedWeekId, selectedOffset } = useWeek();
  const { timezoneName, formatDateTime } = useTimezone();
  const week = useMemo<IWeek>(() => new Week(selectedWeekId, timezoneName), [selectedWeekId, timezoneName]);
  const daysToDisplay = useMemo(
    () => getWeekDaysForCalendar(week.days, selectedDays, selectedOffset, timezoneName),
    [week.days, selectedDays, selectedOffset, timezoneName],
  );

  const isEditAvailability = getIsEditAvailability(search);

  const isDLSMessageVisible = useMemo<boolean>(
    () => week.hasDstTransition && !ignoreDLS,
    [week.hasDstTransition, ignoreDLS],
  );

  useScrollIntoView({
    target: containerRef.current?.querySelector('.timelabel-pill'),
    scrollableObject: containerRef?.current,
    scrollOffset: NAVBAR_HEIGHT,
  });

  return (
    <CalendarContainer
      className={classList((isShowAvailableShifts || isEditAvailability) && 'basket-spacer', 'calendar-container')}
    >
      {isDLSMessageVisible && week?.dst?.date && (
        <CalendarMessageSlot>
          <CalendarMessage
            message={t(`dls.${week.dst.tzOffsetDiff < 0 ? 'end' : 'start'}.infoWeek`, {
              date: formatDateTime(week.dst.date.toISOString(), FULL_WEEKDAY_MONTH_DAY_FORMAT),
            })}
            type={'warning'}
          />
        </CalendarMessageSlot>
      )}
      <CalendarDaysContainer className={classList('calendar-container')}>
        <TimelineColumn
          selectedDays={selectedDays}
          daysToDisplay={daysToDisplay}
          isCalendarMessageVisible={isDLSMessageVisible}
          ignoreDLS={ignoreDLS}
        />
        <CalendarWeekContainer
          ignoreDLS={ignoreDLS}
          isDLSMessageVisible={isDLSMessageVisible}
          week={week}
          daysToDisplay={daysToDisplay}
          Weekday={Weekday}
          isLoading={isLoading}
        />
      </CalendarDaysContainer>
    </CalendarContainer>
  );
};

export { Calendar };

const CalendarMessageSlot = styled.div`
  width: 100%;
  background-color: white;
  position: sticky;
  z-index: ${Z_INDEX_WEEKDAY_HEADER};
  height: ${CALENDAR_MESSAGE_HEIGHT_MOBILE}px;
  top: 0;
  display: flex;
  align-items: flex-start;
  padding: 0 20px 0 ${TIMELINE_COLUMN_WIDTH_MOBILE}px;
  padding-top: 10px;

  ${media.tablet} {
    padding-top: 0;
    align-items: center;
    min-height: 32px;
    height: ${CALENDAR_MESSAGE_HEIGHT}px;
    padding: 20px 20px 0 ${TIMELINE_COLUMN_WIDTH}px;
  }
`;

const CalendarContainer = styled.div`
  padding-top: 20px;
  flex-grow: 1;
  position: relative;
  overflow: visible;

  &.basket-spacer {
    margin-bottom: 32px;
  }
`;

const CalendarDaysContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  flex-grow: 1;
  z-index: 0;
  margin-bottom: 32px;
`;
